'use client'

import * as RUIDialog from '@radix-ui/react-dialog'
import * as React from 'react'

import VideoPlayer from 'component/block/videoPlayer'
import { Image } from 'component/ui/image'
import useLazyRef from 'hook/useLazyRef'
import { createSpotControl } from 'lib/control/spot'
import { idleTimerStore } from 'store/global/idle-timer'
import {
  mutedStore,
  playStateStore,
  useIsMuted,
  useIsUserPaused,
  userPausedStore
} from 'store/global/video'
import { useViewCountStore } from 'store/global/view-count'
import type { IVideoAssetV2 } from 'type/asset'
import type { ISpotV2WithFullContent } from 'type/spot'
import { cn, twc } from 'util/cn'
import SpotInfo from './spot-info'
import SpotTools from './spot-tools'

const PlayContainer = twc.div`absolute bottom-0 left-0 right-0 top-0 z-[2] flex cursor-pointer select-none items-center justify-center drop-shadow-image`

const VoiceButton = twc.div`cursor-pointer absolute right-0 top-0 z-[2] m-[12px] playsee-mb:hidden`

interface ISpot {
  index: number
  spot: ISpotV2WithFullContent
  onIntersect?: (index: number) => void
}
const Spot = (props: ISpot) => {
  const { spot, onIntersect, index } = props

  const [isCommentOpen, setIsCommentOpen] = React.useState(false)
  const onIntersectRef = React.useRef(onIntersect)
  onIntersectRef.current = onIntersect

  const isUserPaused = useIsUserPaused()
  const isMuted = useIsMuted()

  const spotControl = useLazyRef(() =>
    createSpotControl({
      idleTimerStore,
      userPausedStore,
      mutedStore,
      playStateStore,
      useViewCountStore: useViewCountStore.getState()
    })
  )

  const videoCallbackRef = React.useCallback(
    (el: HTMLVideoElement | null) => {
      if (el) {
        spotControl.current.attach(el)
      } else {
        spotControl.current.detach()
      }
    },
    [spotControl]
  )

  const containerRef = React.useRef<HTMLDivElement | null>(null)
  React.useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const observerControl = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            onIntersectRef.current?.(index)
            spotControl.current.onScreen()
          } else {
            spotControl.current.offScreen()
          }
        })
      },
      {
        rootMargin: '0px 0px 0px 0px',
        threshold: 0.5
      }
    )

    observerControl.observe(container)

    return () => {
      observerControl.disconnect()
    }
  }, [index, spotControl])

  const handleCommentOpenChange = React.useCallback((isOpen: boolean) => {
    setIsCommentOpen(isOpen)
    if (isOpen) {
      playStateStore.pause()
    } else {
      playStateStore.play()
    }
  }, [])

  // render
  const videoAsset = spot.asset as IVideoAssetV2
  const loading = index !== 0 ? 'lazy' : 'eager'
  const videoSrc = videoAsset?.video_metadata.display_url
  const posterSrc = videoAsset?.video_metadata.thumbnail?.pixel_720?.url

  return (
    <RUIDialog.Root open={isCommentOpen} onOpenChange={handleCommentOpenChange}>
      <div
        ref={containerRef}
        className='relative mx-auto grid aspect-[398/708] h-full w-fit playsee-mb:aspect-auto playsee-mb:w-full'
      >
        <div className='post-screen relative isolate h-full overflow-hidden rounded-[12px] [grid-column:-1/1] [grid-row:-1/1] playsee-mb:rounded-none playsee-mb:bg-background_still-1st'>
          <div className='relative isolate z-[-1] h-full w-full'>
            {posterSrc && (
              <Image
                src={posterSrc}
                alt=''
                fill={true}
                className='z-[-2] object-cover'
                loading={loading}
              />
            )}
            <VideoPlayer
              playerRef={videoCallbackRef}
              className='absolute left-0 top-0 z-[-1]'
              src={videoSrc}
              preload='metadata'
              autoPlay={index === 0}
              muted
              playsInline
              webkit-playsinline='true'
              onEnded={spotControl.current.play}
              onPlaying={idleTimerStore.stopWatch}
              errorVideoPoster={posterSrc}
            />

            <PlayContainer
              onClick={userPausedStore.toggle}
              role='button'
              aria-label='Play'
            >
              <div
                className='text-background-1st i-ps-play_f [--icon-size:48px] aria-[hidden="true"]:hidden'
                role='img'
                aria-label='Play'
                aria-hidden={!isUserPaused}
              />
            </PlayContainer>
            <VoiceButton
              onClick={mutedStore.toggle}
              role='button'
              aria-label='Mute and Unmute'
            >
              <div
                className='bg-glass_ios-20blur_still rounded-full sq-[24px] flex'
                role='img'
                aria-label='Mute and Unmute'
              >
                <div
                  className={cn(
                    '[--icon-size:16px] text-background-1st m-auto',
                    isMuted ? 'i-ps-volume_off_f' : 'i-ps-volume_on_f'
                  )}
                />
              </div>
            </VoiceButton>
          </div>
          <div className='pointer-events-none absolute bottom-0 left-0 right-0 z-10 h-1/4 bg-gradient-post' />
        </div>
        <SpotInfo
          user={spot.owner!}
          caption={spot.description}
          geoName={spot.place?.name}
          geoId={spot.place?.id}
          share={spot.share?.communities ?? []}
          commentId={spot.comment_id}
        />
        <div
          className={cn(
            'postContainer absolute -right-[--video-padding] bottom-[12px] flex w-[56px] flex-col items-center space-y-[24px] playsee-mb:right-0',
            'text-center text-sm text-label-l1 playsee-mb:text-label_still-l1 [&_svg]:sq-[32px]'
          )}
        >
          <SpotTools
            spotId={spot.id}
            likeCount={spot.counts?.likes ?? 0}
            commentCount={spot.counts?.replies ?? 0}
            isLiked={spot.personal?.is_liked}
            onShareOpen={playStateStore.pause}
            onShareClose={playStateStore.play}
            commentId={spot.comment_id}
          />
        </div>
        {/* Aspect ratio trick base on height
            if safari aspect ratio is working, we can remove this svg
        */}
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='398'
          height='708'
          className='h-full w-auto [grid-column:-1/1] [grid-row:-1/1] playsee-mb:hidden'
        />
      </div>
    </RUIDialog.Root>
  )
}

export default React.memo(Spot)
